import React, { useContext, useEffect, useState } from "react";
import {
    archivePromoCode,
    createPromoCode,
    getPromoCodeTypes,
    getProducts,
    getPromoCode,
    unarchivePromoCode,
    updatePromoCode,
} from "../../lib/api";
import { IProduct, IPromoCode, IPromoCodeType } from "../../types/api";
import { useLoaderData, useLocation, useNavigate } from "react-router";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { displayFormAlert, getUserFromLocalStorage } from "../../lib/utils";
import * as yup from "yup";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { ICreatePromoCodeData } from "../../types/forms";
import { alertActions } from "../../features/alert/alertSlice";
import {
    Button,
    ButtonGroup,
    Col,
    Form,
    InputGroup,
    Modal,
    Row,
} from "react-bootstrap";
import Select from "react-select";
import { yupResolver } from "@hookform/resolvers/yup";
import DatePicker from "react-datepicker";
import { PromoCodesContext } from "./PromoCodes";

const ArchivePromoCodeModal = ({
    show,
    setShow,
    action,
    handleAction,
    promoCodeId,
}: {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    action: string;
    handleAction: (
        action: string,
        promoCodeId: IPromoCode["id"],
    ) => Promise<void>;
    promoCodeId: IPromoCode["id"];
}) => {
    let body: React.JSX.Element = <></>;

    switch (action) {
        case "archive":
            body = (
                <>
                    <h5 className="text-danger text-center">
                        You are about to archive this promo code!
                    </h5>
                    <p className="text-center">
                        This will deactivate it and remove it from admin views
                        (it can always be unarchived later if you wish)
                    </p>
                </>
            );
            break;
        case "unarchive":
            body = (
                <>
                    <h5 className="text-danger text-center">
                        You are about to unarchive this promo code!
                    </h5>
                    <p className="text-center">
                        This will return it to admin views set to an inactive
                        status so you can edit and use it again.
                    </p>
                </>
            );
            break;
    }

    return (
        <Modal show={show} onHide={() => setShow(false)}>
            <Modal.Header>
                <Modal.Title>{`${
                    action.charAt(0).toUpperCase() + action.slice(1)
                } Promo Code`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{body}</Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => setShow(false)}>
                    No
                </Button>
                <Button
                    variant="danger"
                    onClick={() => {
                        handleAction(action, promoCodeId);
                    }}
                >
                    Yes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const loader = async ({ params }: any) => {
    const { promoCodeId } = params;

    const getPromoCodePromise = promoCodeId
        ? getPromoCode(parseInt(promoCodeId))
        : Promise.resolve();

    const [promoCode, { products }, { promoCodeTypes }] = await Promise.all([
        getPromoCodePromise,
        getProducts(true),
        getPromoCodeTypes(),
    ]);

    return {
        promoCode: promoCode?.promoCode || undefined,
        products,
        promoCodeTypes,
        header: promoCodeId ? "Edit Promo Code" : "Add Promo Code",
    };
};

const PromoCode = (): React.JSX.Element => {
    const {
        promoCode: loadedPromoCode,
        products,
        promoCodeTypes,
    } = useLoaderData() as {
        promoCode: IPromoCode | undefined;
        products: IProduct[];
        promoCodeTypes: IPromoCodeType[];
    };

    const location = useLocation();
    const isAddingPromoCode = /tournament-management\/promo-codes\/add/.test(
        location.pathname,
    );

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = getUserFromLocalStorage();
    const { promoCodes, setPromoCodes } = useContext(PromoCodesContext);
    const [promoCode, setPromoCode] = useState<IPromoCode | undefined>(
        loadedPromoCode,
    );
    const [promoCodeCategory, setPromoCodeCategory] = useState<
        "fixed" | "percent" | undefined
    >(promoCode?.promoCodeType ? promoCode.promoCodeType.category : undefined);
    const [showModal, setShowModal] = useState<boolean>(false);

    const createPromoCodeFormSchema = yup.object().shape({
        code: yup.string().required("Promo Code is required"),
        description: yup.string().optional().nullable(),
        discount: yup
            .number()
            .min(0, "Discount must be greater than 0")
            .typeError("Invalid Discount")
            .required(),
        maxUses: yup
            .number()
            .optional()
            .typeError("Invalid Max Uses")
            .transform((value, originalValue) => {
                return originalValue === "" ? undefined : value;
            }),
        promoCodeType: yup.object().shape({
            value: yup.number().required(),
            label: yup.string().required("Please select the promo code type"),
        }),
        appliesTo: yup
            .array(
                yup.object().shape({
                    value: yup.number().required(),
                    label: yup.string().required(),
                }),
            )
            .test(
                "mutually-exclusive",
                "If the coupon is applied to specific products, it can't also be excluded from specific products.",
                function (appliesTo) {
                    const { excludes } = this.parent;
                    return !(appliesTo?.length && excludes?.length);
                },
            ),
        excludes: yup
            .array(
                yup.object().shape({
                    value: yup.number().required(),
                    label: yup.string().required(),
                }),
            )
            .test(
                "mutually-exclusive",
                "If the coupon is excluded from specific products, it can't also be applied to specific products.",
                function (excludes) {
                    const { appliesTo } = this.parent;
                    return !(appliesTo?.length && excludes?.length);
                },
            ),
        active: yup.number().oneOf([0, 1]).required(),
        startDate: yup.date().optional().nullable(),
        endDate: yup.date().optional().nullable(),
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        reset,
        watch,
        setValue,
        getValues,
        trigger,
    } = useForm<ICreatePromoCodeData>({
        mode: "onChange",
        shouldUnregister: true,
        resolver: yupResolver(createPromoCodeFormSchema),
        defaultValues: {
            code: promoCode?.code || undefined,
            active: promoCode?.active,
            description: promoCode?.description || undefined,
            discount: promoCode?.discount || undefined,
            maxUses: promoCode?.maxUses || undefined,
            startDate: promoCode?.startDate
                ? new Date(promoCode.startDate)
                : undefined,
            endDate: promoCode?.endDate
                ? new Date(promoCode.endDate)
                : undefined,
            promoCodeType: promoCode?.promoCodeType
                ? {
                      value: parseInt(promoCode?.promoCodeType.id.toString()),
                      label: promoCode?.promoCodeType.name,
                  }
                : {},
            appliesTo: promoCode?.appliesTo
                ? promoCode.appliesTo
                      .filter(pc => {
                          return products.some(p => p.id === pc);
                      })
                      .map(pc => {
                          return {
                              value: parseInt(pc.toString()),
                              label: products.find(p => p.id == pc)
                                  ?.description as string,
                          };
                      })
                : [],
            excludes: promoCode?.excludes
                ? promoCode.excludes
                      .filter(pc => {
                          return products.some(p => p.id === pc);
                      })
                      .map(pc => {
                          return {
                              value: parseInt(pc.toString()),
                              label: products.find(p => p.id == pc)
                                  ?.description as string,
                          };
                      })
                : [],
        },
    });

    const onSubmit: SubmitHandler<ICreatePromoCodeData> = async data => {
        isAddingPromoCode
            ? await createPromoCode(data, user!.id)
                  .then(response => {
                      dispatch(
                          alertActions.success({
                              title: "Promo Code created successfully",
                              dismissible: true,
                          }),
                      );
                      setPromoCodes(response.promoCodes);
                      reset();
                  })
                  .catch(e => {
                      dispatch(
                          alertActions.error({
                              title: "The server encountered a problem creating the promoCode.",
                              text: "If the problem continues, please reach out to an admin.",
                              dismissible: true,
                          }),
                      );
                  })
            : await updatePromoCode(data, promoCode!.id, user!.id)
                  .then(response => {
                      dispatch(
                          alertActions.success({
                              title: "Promo Code updated successfully",
                              dismissible: true,
                          }),
                      );
                      setPromoCode(
                          response.promoCodes.find(p => p.id === promoCode?.id),
                      );
                      setPromoCodes(response.promoCodes);
                  })
                  .catch(e => {
                      dispatch(
                          alertActions.error({
                              title: "The server encountered a problem creating the promoCode.",
                              text: "If the problem continues, please reach out to an admin.",
                              dismissible: true,
                          }),
                      );
                  });
    };

    const handleArchive = async (
        action: string,
        promoCodeId: IPromoCode["id"],
    ) => {
        action === "archive"
            ? await archivePromoCode(promoCodeId, user!.id)
                  .then(response => {
                      dispatch(
                          alertActions.success({
                              title: "Promo Code archived successfully",
                              dismissible: true,
                              persist: true,
                          }),
                      );
                      setPromoCodes(response.promoCodes);
                      navigate("/tournament-management/promo-codes");
                  })
                  .catch(e => {
                      dispatch(
                          alertActions.error({
                              title: "The server encountered a problem archiving the promoCode.",
                              text: "If the problem continues, please reach out to an admin.",
                              dismissible: true,
                          }),
                      );
                  })
            : await unarchivePromoCode(promoCodeId, user!.id)
                  .then(response => {
                      dispatch(
                          alertActions.success({
                              title: "Promo Code unarchived successfully",
                              dismissible: true,
                              persist: true,
                          }),
                      );
                      setPromoCodes(response.promoCodes);
                      navigate("/tournament-management/promo-codes");
                  })
                  .catch(e => {
                      dispatch(
                          alertActions.error({
                              title: "The server encountered a problem unarchiving the promoCode.",
                              text: "If the problem continues, please reach out to an admin.",
                              dismissible: true,
                          }),
                      );
                  });
    };

    useEffect(() => {
        const subscription = watch((data, { name, type }) => {
            if (name === "promoCodeType" && type == "change") {
                const promoCodeType = promoCodeTypes.find(
                    t =>
                        data.promoCodeType &&
                        t.id === data.promoCodeType?.value,
                );

                setPromoCodeCategory(promoCodeType?.category);
            }
        });

        return () => subscription.unsubscribe();
    }, [watch]);

    const isPromoCodeActive = isAddingPromoCode
        ? watch("active", 1)
        : watch("active", promoCode?.active);

    const isArchived = promoCode?.active === -1;
    const watchAppliesTo = useWatch({
        control,
        name: "appliesTo",
    });
    const watchExcludes = useWatch({
        control,
        name: "excludes",
    });

    return (
        <section
            className="createPromoCodeInfo"
            data-testid="createPromoCodeContent"
        >
            <Form onSubmit={handleSubmit(onSubmit)} id="createGameForm">
                <Row>
                    <Form.Group
                        controlId="promoCodeType"
                        as={Col}
                        xs={6}
                        lg={4}
                        className="col"
                    >
                        <Form.Label>Promo Code Type</Form.Label>
                        <Controller
                            name="promoCodeType"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    isSearchable={false}
                                    options={promoCodeTypes.map(t => {
                                        return {
                                            value: parseInt(t.id.toString()),
                                            label: t.name,
                                        };
                                    })}
                                    isDisabled={isArchived}
                                />
                            )}
                        />
                        {errors.promoCodeType &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.promoCodeType.label
                                    ?.message as string,
                            })}
                    </Form.Group>
                    <Form.Group controlId="code" as={Col} sm={6} lg={4} xl={3}>
                        <Form.Label>Promo Code</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("code")}
                            aria-invalid={errors.code ? "true" : "false"}
                            disabled={isArchived}
                        />
                        {errors.code &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.code?.message as string,
                            })}
                    </Form.Group>
                    <Form.Group controlId="description" as={Col} sm={6} lg={4}>
                        <Form.Label>
                            Description{" "}
                            <span className="fw-normal">(optional)</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            {...register("description")}
                            aria-invalid={errors.description ? "true" : "false"}
                            disabled={isArchived}
                        />
                        {errors.description &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.description?.message as string,
                            })}
                    </Form.Group>
                    <Form.Group
                        controlId="discount"
                        as={Col}
                        sm={6}
                        lg={4}
                        xl={3}
                    >
                        <Form.Label>Discount</Form.Label>
                        <InputGroup className="mb-2">
                            <InputGroup.Text>
                                {promoCodeCategory
                                    ? promoCodeCategory === "fixed"
                                        ? "$"
                                        : "%"
                                    : ""}
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                {...register("discount")}
                                aria-invalid={
                                    errors.discount ? "true" : "false"
                                }
                                disabled={isArchived}
                            />
                        </InputGroup>
                        {errors.discount &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.discount?.message as string,
                            })}
                    </Form.Group>
                    <Form.Group controlId="maxUses" as={Col} xs={6} lg={3}>
                        <Form.Label>
                            Max Uses{" "}
                            <span className="fw-normal">(optional)</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            {...register("maxUses")}
                            aria-invalid={errors.maxUses ? "true" : "false"}
                            disabled={isArchived}
                        />
                        {errors.maxUses &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.maxUses.message as string,
                            })}
                    </Form.Group>
                    <Form.Group
                        controlId="startDate"
                        as={Col}
                        sm={6}
                        lg={4}
                        xl={3}
                    >
                        <Form.Label>
                            Start Date{" "}
                            <span className="fw-normal">(optional)</span>
                        </Form.Label>
                        <br />
                        <Controller
                            name="startDate"
                            control={control}
                            render={() => (
                                <DatePicker
                                    selected={watch(
                                        "startDate",
                                        promoCode?.startDate
                                            ? new Date(promoCode.startDate)
                                            : null,
                                    )}
                                    onChange={date => {
                                        setValue("startDate", date as Date, {
                                            shouldValidate: true,
                                        });

                                        const endDate = getValues("endDate");
                                        date &&
                                            endDate &&
                                            date > endDate &&
                                            setValue("endDate", null, {
                                                shouldValidate: true,
                                            });
                                    }}
                                    showMonthDropdown
                                    showYearDropdown
                                    showTimeSelect
                                    timeFormat="h:mm aa"
                                    timeIntervals={60}
                                    timeCaption="Time"
                                    dateFormat="MMMM d, yyyy h:mm aa"
                                    dropdownMode="select"
                                    className="form-control"
                                    isClearable
                                    disabled={isArchived}
                                />
                            )}
                        />
                        {errors.startDate &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.startDate?.message as string,
                                settings: "mb-0",
                            })}
                    </Form.Group>
                    <Form.Group
                        controlId="endDate"
                        as={Col}
                        sm={6}
                        lg={4}
                        xl={3}
                    >
                        <Form.Label>
                            End Date{" "}
                            <span className="fw-normal">(optional)</span>
                        </Form.Label>
                        <br />
                        <Controller
                            name="endDate"
                            control={control}
                            render={() => (
                                <DatePicker
                                    selected={watch(
                                        "endDate",
                                        promoCode?.endDate
                                            ? new Date(promoCode.endDate)
                                            : null,
                                    )}
                                    onChange={date =>
                                        setValue("endDate", date as Date, {
                                            shouldValidate: true,
                                        })
                                    }
                                    showMonthDropdown
                                    showYearDropdown
                                    showTimeSelect
                                    timeFormat="h:mm aa"
                                    timeIntervals={60}
                                    timeCaption="Time"
                                    dateFormat="MMMM d, yyyy h:mm aa"
                                    dropdownMode="select"
                                    className="form-control"
                                    isClearable
                                    minDate={
                                        getValues("startDate")
                                            ? getValues("startDate")
                                            : null
                                    }
                                    disabled={isArchived}
                                />
                            )}
                        />
                        {errors.endDate &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.endDate?.message as string,
                                settings: "mb-0",
                            })}
                    </Form.Group>
                </Row>
                <Row>
                    <Form.Group
                        controlId="appliesTo"
                        as={Col}
                        xs={6}
                        className="col"
                    >
                        <Form.Label>
                            Applies To{" "}
                            <span className="fw-normal">(optional)</span>
                        </Form.Label>
                        <Controller
                            name="appliesTo"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    isMulti
                                    isSearchable={true}
                                    options={products.map(p => {
                                        return {
                                            value: parseInt(p.id.toString()),
                                            label: p.description,
                                        };
                                    })}
                                    isDisabled={
                                        isArchived ||
                                        (watchExcludes &&
                                            watchExcludes?.length !== 0)
                                    }
                                    onChange={value => {
                                        field.onChange(value);
                                        trigger("excludes");
                                    }}
                                />
                            )}
                        />
                        {errors.appliesTo &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.appliesTo?.message as string,
                                settings: "mb-0",
                            })}
                        <Form.Text>
                            Use this to select only the products this coupon{" "}
                            <b>does</b> apply to. Leaving this empty will make
                            this promo code apply to all products. This is
                            mutually exclusive with the "Excludes" field, only
                            one or the other can be set.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group
                        controlId="excludes"
                        as={Col}
                        xs={6}
                        className="col"
                    >
                        <Form.Label>
                            Excludes{" "}
                            <span className="fw-normal">(optional)</span>
                        </Form.Label>
                        <Controller
                            name="excludes"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    isMulti
                                    isSearchable={true}
                                    options={products.map(p => {
                                        return {
                                            value: parseInt(p.id.toString()),
                                            label: p.description,
                                        };
                                    })}
                                    isDisabled={
                                        isArchived ||
                                        (watchAppliesTo &&
                                            watchAppliesTo.length !== 0)
                                    }
                                    onChange={value => {
                                        field.onChange(value);
                                        trigger("appliesTo");
                                    }}
                                />
                            )}
                        />
                        {errors.excludes &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.excludes?.message as string,
                                settings: "mb-0",
                            })}
                        <Form.Text>
                            Use this to select only the products this coupon{" "}
                            <b>does not</b> apply to, all other products will be
                            included in this promo code. Leaving this empty will
                            make this promo code apply to all products. This is
                            mutually exclusive with the "Applies To" field, only
                            one or the other can be set.
                        </Form.Text>
                    </Form.Group>
                </Row>
                <Row>
                    {!isArchived && (
                        <Form.Group
                            controlId="active"
                            as={Col}
                            xs={{ order: "last" }}
                            md={{ span: 3, order: "first" }}
                            className="align-content-end"
                        >
                            <Form.Control
                                type="hidden"
                                {...register("active", {
                                    value: isPromoCodeActive,
                                })}
                            />
                            <ButtonGroup>
                                <Button
                                    variant={
                                        isPromoCodeActive
                                            ? "primary"
                                            : "outline-primary"
                                    }
                                    onClick={() => setValue("active", 1)}
                                    disabled={isArchived}
                                >
                                    Active
                                </Button>
                                <Button
                                    variant={
                                        isPromoCodeActive
                                            ? "outline-primary"
                                            : "primary"
                                    }
                                    onClick={() => setValue("active", 0)}
                                    disabled={isArchived}
                                >
                                    Inactive
                                </Button>
                            </ButtonGroup>
                        </Form.Group>
                    )}
                </Row>
                {!isArchived && (
                    <Row>
                        <Col>
                            <Button
                                variant="primary"
                                type="submit"
                                className="mt-4"
                                disabled={isArchived}
                            >
                                {isAddingPromoCode ? "Create" : "Update"} Promo
                                Code
                            </Button>
                        </Col>
                    </Row>
                )}
                {!isAddingPromoCode && (
                    <>
                        <Row>
                            <Col>
                                <Button
                                    variant="danger"
                                    type="button"
                                    className="mt-4"
                                    onClick={() => setShowModal(true)}
                                >
                                    {promoCode?.active !== -1
                                        ? "Archive"
                                        : "Unarchive"}{" "}
                                    Promo Code
                                </Button>
                            </Col>
                        </Row>
                        <ArchivePromoCodeModal
                            show={showModal}
                            setShow={setShowModal}
                            promoCodeId={promoCode?.id as IPromoCode["id"]}
                            action={
                                promoCode?.active !== -1
                                    ? "archive"
                                    : "unarchive"
                            }
                            handleAction={handleArchive}
                        />
                    </>
                )}
            </Form>
        </section>
    );
};

export default PromoCode;
